if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this._qtyLockedPending = false;
      }

      connectedCallback() {
        if (!this.isQtyLocked) {
          this.updateQtyLockedState();
          return;
        }

        const variantId = this.variantIdInput?.value;
        if (!variantId) {
          this.updateQtyLockedState();
          return;
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId === variantId) return;
          this.refreshQtyLockedState(variantId);
        });

        this.refreshQtyLockedState(variantId);
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
      }

      refreshQtyLockedState(variantId) {
        return this.fetchVariantCartQuantity(variantId)
          .then((cartQty) => {
            this.setCartQuantity(cartQty);
            this.updateQtyLockedState();
          })
          .catch(() => {
            this.updateQtyLockedState();
          });
      }

      get isQtyLocked() {
        return this.dataset.qtyLocked === 'true';
      }

      get quantityInput() {
        return this.closest('product-info')?.querySelector('.quantity__input');
      }

      getCartQuantity() {
        const input = this.quantityInput;
        if (input) {
          return parseInt(input.dataset.cartQuantity || '0', 10);
        }
        return parseInt(this.dataset.cartQuantity || '0', 10);
      }

      setCartQuantity(quantity) {
        if (this.quantityInput) {
          this.quantityInput.dataset.cartQuantity = String(quantity);
        }
        this.dataset.cartQuantity = String(quantity);
      }

      get purchaseLimit() {
        return parseInt(this.dataset.purchaseLimit || '1', 10);
      }

      get purchasedCount() {
        return parseInt(this.dataset.purchasedCount || '0', 10);
      }

      isPurchaseLimitReached() {
        return this.isQtyLocked && this.purchasedCount >= this.purchaseLimit && this.getCartQuantity() === 0;
      }

      isQtyLockedAtLimit() {
        if (!this.isQtyLocked) return false;
        if (this.isPurchaseLimitReached()) return true;
        return this.getCartQuantity() >= 1;
      }

      getQtyLockedBlockedLabel() {
        if (this.isPurchaseLimitReached()) {
          return this.dataset.qtyLockedLimitLabel || window.variantStrings.qtyLockedLimitReached;
        }
        return this.dataset.qtyLockedInCartLabel || window.variantStrings.qtyLockedInCart;
      }

      // Qty lock is per product: one unit total across every size/variant.
      matchesLockedProduct(item, variantId) {
        if (!item) return false;
        const productId = this.dataset.productId;
        return productId
          ? String(item.product_id) === String(productId)
          : String(item.variant_id) === String(variantId);
      }

      async fetchVariantCartQuantity(variantId) {
        const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
        return cart.items
          .filter((item) => this.matchesLockedProduct(item, variantId))
          .reduce((total, item) => total + item.quantity, 0);
      }

      getQuantityFromResponse(response, variantId) {
        if (Array.isArray(response.items)) {
          const items = response.items.filter((lineItem) => this.matchesLockedProduct(lineItem, variantId));
          if (items.length) return items.reduce((total, item) => total + item.quantity, 0);
        }

        return this.matchesLockedProduct(response, variantId) ? response.quantity ?? 0 : 0;
      }

      updateCartQuantityFromResponse(response, variantId) {
        const quantity = this.getQuantityFromResponse(response, variantId);
        if (quantity > 0) {
          this.setCartQuantity(quantity);
        }
      }

      // shortcut: only trims the variant just submitted, other sizes are blocked by the pre-add guard;
      // upgrade to removing every line of the product if a race ever lands two sizes in one cart.
      async enforceQtyLockedLimit(variantId) {
        const sections = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : ['cart-icon-bubble'];

        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
        return response.json();
      }

      updateQtyLockedState() {
        if (!this.isQtyLocked || !this.submitButton || !this.submitButtonText) return;

        const atLimit = this.isQtyLockedAtLimit();
        const label = this.getQtyLockedBlockedLabel();
        const blockedLabels = [
          this.dataset.qtyLockedInCartLabel,
          this.dataset.qtyLockedLimitLabel,
          window.variantStrings.qtyLockedInCart,
          window.variantStrings.qtyLockedLimitReached,
        ].filter(Boolean);

        if (atLimit) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButtonText.textContent = label;
          return;
        }

        if (blockedLabels.includes(this.submitButtonText.textContent)) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.dataset.redeemBlocked === 'true') {
          this.handleErrorMessage(this.dataset.redeemBlockedMessage || window.variantStrings.redeemAlreadyRedeemed);
          return;
        }
        if (this.isQtyLocked && this._qtyLockedPending) return;

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const requestedQty = parseInt(formData.get('quantity') || '1', 10);

        const donationPicker = this.donationPicker;
        if (donationPicker && !donationPicker.validate()) return;

        if (this.isQtyLocked) {
          let cartQty = this.getCartQuantity();

          try {
            cartQty = await this.fetchVariantCartQuantity(variantId);
            this.setCartQuantity(cartQty);
          } catch (error) {
            console.error(error);
          }

          if (cartQty + requestedQty > 1) {
            this.handleErrorMessage(window.variantStrings.qtyLockedError);
            this.updateQtyLockedState();
            return;
          }

          if (this.purchasedCount + cartQty + requestedQty > this.purchaseLimit) {
            this.handleErrorMessage(window.variantStrings.qtyLockedLimitError);
            this.updateQtyLockedState();
            return;
          }

          this._qtyLockedPending = true;
          this.updateQtyLockedState();
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const donationItems = donationPicker ? donationPicker.items : [];

        if (donationItems.length) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(this.buildCartPayload(formData, donationItems));
        } else {
          config.body = formData;
        }

        try {
          let response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }

          if (donationItems.length) {
            response = await this.normalizeCartResponse(response, variantId);
          }

          if (this.isQtyLocked) {
            let responseQty = this.getQuantityFromResponse(response, variantId);

            if (responseQty > 1) {
              response = await this.enforceQtyLockedLimit(variantId);
              responseQty = this.getQuantityFromResponse(response, variantId) || 1;
            }

            this.setCartQuantity(responseQty);
            this.updateQtyLockedState();
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          if (!this.error) {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure('add:paint-updated-sections', () => {
                    this.cart.renderContents(response);
                  });
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          }
        } catch (error) {
          console.error(error);
        } finally {
          if (this.isQtyLocked) {
            this._qtyLockedPending = false;
          }

          if (!this.error) donationPicker?.reset();

          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error && !this.isQtyLockedAtLimit()) {
            this.submitButton.removeAttribute('aria-disabled');
          }
          if (this.isQtyLocked) this.updateQtyLockedState();
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else if (!this.isQtyLockedAtLimit()) {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get donationPicker() {
        if (this.closest('quick-add-modal')) return null;

        const picker = this.closest('product-info')?.querySelector('donation-picker');
        return typeof picker?.validate === 'function' ? picker : null;
      }

      buildCartPayload(formData, donationItems) {
        const item = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan);

        const properties = {};
        formData.forEach((value, key) => {
          const property = key.match(/^properties\[(.+)\]$/);
          if (property) properties[property[1]] = value;
        });
        if (Object.keys(properties).length) item.properties = properties;

        const payload = { items: [item, ...donationItems] };
        const sections = formData.get('sections');

        if (sections) {
          payload.sections = sections;
          payload.sections_url = formData.get('sections_url');
        }

        return payload;
      }

      /**
       * Multi-item adds respond with an `items` array, while the cart drawer and
       * notification expect the line item of the product that was added.
       */
      async normalizeCartResponse(response, variantId) {
        if (!Array.isArray(response.items)) return response;

        const addedItem =
          response.items.find((item) => String(item.variant_id) === String(variantId)) || response.items[0];
        const sections = response.sections || (await this.fetchCartSections());

        return { ...addedItem, items: response.items, sections };
      }

      async fetchCartSections() {
        if (!this.cart) return undefined;

        const sections = this.cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');

        try {
          const url = `${window.location.pathname}?sections=${encodeURIComponent(sections)}`;
          return await fetch(url).then((res) => res.json());
        } catch (error) {
          console.error(error);
          return undefined;
        }
      }
    }
  );
}
